/**
 * Centralized API Client with Automatic JWT Token Refresh & Guest Session Header
 * Intercepts 401 responses, silently refreshes expired access tokens,
 * and retries the failed requests.
 */

import { getApiUrl } from "@/lib/api-config";
import { getAuthToken, setAuthToken, clearAuthToken } from "@/lib/auth-token";

let refreshPromise: Promise<string | null> | null = null;

function getOrCreateGuestSessionUuid(): string {
  if (typeof window === "undefined") return "";
  try {
    let uuid = localStorage.getItem("ik_guest_session_uuid");
    if (!uuid) {
      uuid = crypto.randomUUID();
      localStorage.setItem("ik_guest_session_uuid", uuid);
    }
    return uuid;
  } catch {
    return "";
  }
}

async function doRefreshToken(): Promise<string | null> {
  const currentToken = getAuthToken();
  if (!currentToken) return null;

  try {
    const res = await fetch(getApiUrl("/api/v1/auth/refresh"), {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${currentToken}`,
      },
    });

    if (!res.ok) {
      clearAuthToken();
      return null;
    }

    const json = await res.json();
    if (json.success && json.data?.access_token) {
      const newToken = json.data.access_token;
      setAuthToken(newToken);
      return newToken;
    }
    clearAuthToken();
    return null;
  } catch {
    clearAuthToken();
    return null;
  }
}

export async function apiFetch(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const url = getApiUrl(endpoint);
  const headers = new Headers(options.headers || {});

  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }

  const token = getAuthToken();
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const guestUuid = getOrCreateGuestSessionUuid();
  if (guestUuid && !headers.has("X-Guest-Session-Uuid")) {
    headers.set("X-Guest-Session-Uuid", guestUuid);
  }

  let fetchOptions = { ...options };
  const method = fetchOptions.method?.toUpperCase();
  if (method === "PUT" || method === "PATCH" || method === "DELETE") {
    headers.set("X-HTTP-Method-Override", method);
    fetchOptions.method = "POST";
  }

  let res = await fetch(url, { credentials: "include", ...fetchOptions, headers });

  const isAuthEndpoint =
    endpoint.includes("/auth/login") ||
    endpoint.includes("/auth/otp/verify") ||
    endpoint.includes("/auth/signup") ||
    endpoint.includes("/auth/refresh");

  if (res.status === 401 && !isAuthEndpoint && getAuthToken()) {
    if (!refreshPromise) {
      refreshPromise = doRefreshToken().finally(() => {
        refreshPromise = null;
      });
    }

    const newToken = await refreshPromise;
    if (newToken) {
      headers.set("Authorization", `Bearer ${newToken}`);
      res = await fetch(url, { credentials: "include", ...fetchOptions, headers });
    }
  }

  return res;
}

/**
 * Helper that performs apiFetch, parses JSON, and throws standardized error objects if status is not OK.
 */
export async function apiFetchJson<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const res = await apiFetch(endpoint, options);
  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw { ...json, status: res.status };
  }

  return json as T;
}
